// ═══════════════════════════════════════════════════════════════════════════════
// Shared modal shell — backdrop + panel (see Doc/frontend_ui_style_guide.md §4.3)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  MODAL_BACKDROP_SCRIM_CLASS,
  MODAL_PANEL_SCROLL_DVH_CLASS,
  MODAL_PANEL_SURFACE_CLASS,
} from '../../lib/modalClasses';
import { ModalCircleCloseButton } from './ModalCircleCloseButton';

type AppModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  titleId?: string;
  /** Accessible name when title is not rendered as visible heading. */
  ariaLabel?: string;
  /** Optional aria-describedby id (e.g. confirm description). */
  describedById?: string;
  children: ReactNode;
  /** Hide the top-right close control. */
  hideCloseButton?: boolean;
  closeAriaLabel?: string;
  /** When true, Escape / backdrop do not close. */
  closeDisabled?: boolean;
  backdropZClass?: string;
  panelClassName?: string;
  /**
   * Replaces default `max-w-md` on the panel surface (e.g. `max-w-lg`, `max-w-4xl`).
   * Prefer this over putting another `max-w-*` in `panelClassName` (no twMerge).
   */
  panelMaxWidthClass?: string;
  /**
   * Replaces default scroll shell (`MODAL_PANEL_SCROLL_DVH_CLASS`).
   * Use e.g. `max-h-[calc(100dvh-2rem)] overflow-hidden` when the body scrolls inside.
   */
  panelScrollClassName?: string;
  /** Extra content in the header row (beside title / close). */
  headerEnd?: ReactNode;
};

const BACKDROP_BASE =
  'fixed inset-0 flex items-center justify-center overflow-y-auto overscroll-y-contain p-4';

export const AppModal = ({
  open,
  onClose,
  title,
  titleId: titleIdProp,
  ariaLabel,
  describedById,
  children,
  hideCloseButton = false,
  closeAriaLabel = 'Close',
  closeDisabled = false,
  backdropZClass = 'z-[60]',
  panelClassName = '',
  panelMaxWidthClass = 'max-w-md',
  panelScrollClassName = MODAL_PANEL_SCROLL_DVH_CLASS,
  headerEnd,
}: AppModalProps) => {
  const generatedTitleId = useId();
  const titleId = titleIdProp ?? generatedTitleId;

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (closeDisabled) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose, closeDisabled]);

  if (!open) {
    return null;
  }

  // Avoid an empty title row that only holds the close control (causes top blank space).
  const hasHeaderLeading = Boolean(title) || Boolean(headerEnd);
  const showHeaderBar = hasHeaderLeading;
  const showFloatingClose = !hideCloseButton && !showHeaderBar;
  const showHeaderClose = !hideCloseButton && showHeaderBar;
  const panelSurfaceClass = MODAL_PANEL_SURFACE_CLASS.replace(/\bmax-w-md\b/, panelMaxWidthClass);
  const panelBaseClass = `relative z-10 ${panelSurfaceClass} ${panelScrollClassName}`;

  return createPortal(
    <div className={`${BACKDROP_BASE} ${backdropZClass}`} role="presentation">
      <button
        type="button"
        className={`absolute inset-0 disabled:cursor-not-allowed ${MODAL_BACKDROP_SCRIM_CLASS}`}
        aria-label={closeAriaLabel}
        disabled={closeDisabled}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={describedById}
        aria-label={!title ? ariaLabel ?? closeAriaLabel : undefined}
        className={`${panelBaseClass} ${panelClassName}`.trim()}
      >
        {showFloatingClose ? (
          <ModalCircleCloseButton
            className="absolute right-3 top-3 z-20"
            ariaLabel={closeAriaLabel}
            disabled={closeDisabled}
            onClick={onClose}
          />
        ) : null}
        {showHeaderBar ? (
          <div className="mb-3 flex items-start justify-between gap-3">
            {title ? (
              <h2 id={titleId} className="min-w-0 flex-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </h2>
            ) : (
              <span className="min-w-0 flex-1" />
            )}
            <div className="flex shrink-0 items-center gap-2">
              {headerEnd}
              {showHeaderClose ? (
                <ModalCircleCloseButton
                  ariaLabel={closeAriaLabel}
                  disabled={closeDisabled}
                  onClick={onClose}
                />
              ) : null}
            </div>
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
};
